import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Layers, Globe, User, PlayCircle, Lock, CheckCircle2, FileText, Gift } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { Disclaimer } from "@/components/Shared";
import { useAuth } from "@/context/AuthContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CourseReviews } from "@/components/CourseReviews";

const ytId = (url) => { const m = (url || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/); return m ? m[1] : ""; };
const vimeoId = (url) => { const m = (url || "").match(/vimeo\.com\/(?:video\/)?(\d+)/); return m ? m[1] : ""; };
const loadRazorpay = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => res(true); s.onerror = () => res(false);
  document.body.appendChild(s);
});

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const [checkoutEnabled, setCheckoutEnabled] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    api.get("/checkout/config").then((r) => setCheckoutEnabled(!!r.data.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) api.get("/my/enrollments").then((r) => setEnrolled(r.data.some((e) => e.course?.slug === slug))).catch(() => {});
    else setEnrolled(false);
  }, [user, slug]);

  const handleAccess = async () => {
    if (!user) { navigate("/login"); return; }
    if (enrolled) { navigate(`/courses/${slug}/learn`); return; }
    if (course.status === "coming_soon") { toast.info("This course is coming soon. We'll notify you."); return; }
    const paid = (course.price || 0) > 0;
    if (!paid) {
      try {
        await api.post("/enroll", { course_slug: slug });
        toast.success("Enrolled! Opening your classroom.");
        navigate(`/courses/${slug}/learn`);
      } catch (e) { toast.error(apiErr(e)); }
      return;
    }
    // paid → Razorpay
    if (!checkoutEnabled) { toast.info("Online payment is being set up. Please contact us to enrol."); navigate("/contact"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/courses/checkout/order", { course_slug: slug });
      const ok = await loadRazorpay();
      if (!ok) { setBusy(false); return toast.error("Could not load the payment window."); }
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: "KRV Prime", description: data.course_title,
        prefill: { name: data.user_name, email: data.user_email },
        theme: { color: "#D4AF37" },
        handler: async (r) => {
          try {
            await api.post("/courses/checkout/verify", { ...r, course_slug: slug });
            toast.success("Payment successful! Opening your classroom.");
            setEnrolled(true);
            navigate(`/courses/${slug}/learn`);
          } catch { toast.error("Payment could not be verified. If money was deducted, contact us."); }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  if (notFound) return <div className="py-32 text-center text-slate-400" data-testid="course-not-found">Course not found. <Link to="/courses" className="text-navy underline">Back to courses</Link></div>;
  if (!course) return <div className="py-32 text-center text-slate-400">Loading…</div>;

  const free = course.price === 0;

  return (
    <div data-testid="course-detail-page">
      <PageHero
        eyebrow={course.category}
        title={course.title}
        subtitle={course.subtitle}
        crumbs={[{ label: "Courses", to: "/courses" }, { label: course.title }]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-12">
          <div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              <span className="inline-flex items-center gap-1.5"><Layers className="h-4 w-4 text-gold" />{course.level}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-gold" />{course.duration}</span>
              <span className="inline-flex items-center gap-1.5"><Globe className="h-4 w-4 text-gold" />{course.language}</span>
              <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4 text-gold" />{course.instructor}</span>
            </div>
            <p className="mt-6 text-slate-600 leading-relaxed">{course.description}</p>
          </div>

          <div className="rounded-lg border border-gold/40 bg-gold/5 p-4 text-sm text-slate-600" data-testid="course-edu-notice">
            <span className="font-semibold text-navy">Educational Notice:</span> This course is intended for educational purposes. Examples, methodologies, historical scenarios and strategy frameworks are provided to explain financial-market concepts. They do not constitute personalised investment advice, a guarantee of profit, or a guarantee of future performance.
          </div>
          {/(opt|future|deriv|f&o|greek)/i.test(`${course.category || ""} ${course.title || ""} ${course.level || ""}`) && (
            <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-4 text-sm text-slate-600" data-testid="course-risk-warning">
              <span className="font-semibold text-rose-700">Risk Warning:</span> Futures and options involve substantial risk and may result in significant financial loss. Historical examples and strategy illustrations do not guarantee future results.
            </div>
          )}

          {course.bonus_event && (
            <div className="rounded-lg border border-gold/40 bg-gold/5 p-4 flex items-start gap-3" data-testid="course-bonus">
              <Gift className="h-5 w-5 text-gold shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700"><span className="font-semibold text-navy">Bonus included:</span> the recorded webinar <span className="font-medium">"{course.bonus_event.title}"</span> — free with this course, watchable anytime from your dashboard.</p>
            </div>
          )}

          {course.outcomes?.length > 0 && (
            <div>
              <h2 className="font-display text-2xl font-bold text-navy mb-5">Learning Outcomes</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {course.outcomes.map((o, i) => (
                  <div key={i} className="flex gap-3 text-sm text-slate-600"><CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />{o}</div>
                ))}
              </div>
            </div>
          )}

          {course.curriculum?.length > 0 && (
            <div>
              <h2 className="font-display text-2xl font-bold text-navy mb-5">Curriculum</h2>
              <Accordion type="single" collapsible className="border border-border rounded-lg divide-y divide-border" data-testid="curriculum-accordion">
                {course.curriculum.map((mod, i) => (
                  <AccordionItem key={i} value={`m-${i}`} className="border-0">
                    <AccordionTrigger className="px-5 hover:no-underline">
                      <span className="text-left font-medium text-navy">Module {i + 1}: {mod.title}</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-5">
                      <ul className="space-y-2">
                        {mod.lessons.map((l, j) => {
                          const canPreview = l.preview && l.has_video;
                          return (
                          <li key={j} className={`flex items-center justify-between text-sm py-1.5 ${canPreview ? "cursor-pointer text-navy hover:text-gold" : "text-slate-600"}`}
                              onClick={() => canPreview && setPreview(l)} data-testid={canPreview ? `preview-lesson-${l.id}` : undefined}>
                            <span className="inline-flex items-center gap-2">
                              {l.preview ? <PlayCircle className={`h-4 w-4 ${l.has_video ? "text-gold" : "text-slate-300"}`} /> : <Lock className="h-4 w-4 text-slate-300" />}
                              {l.title}
                            </span>
                            <span className="text-xs text-slate-400">{l.duration}{l.preview && " · Preview"}</span>
                          </li>
                          );
                        })}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {course.format?.length > 0 && (
            <div>
              <h2 className="font-display text-2xl font-bold text-navy mb-5">Course Format</h2>
              <div className="flex flex-wrap gap-2">
                {course.format.map((f) => (
                  <span key={f} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-subtle border border-border text-slate-600">
                    <FileText className="h-3.5 w-3.5 text-gold" />{f}
                  </span>
                ))}
              </div>
            </div>
          )}

          <CourseReviews slug={slug} canReview={enrolled} />

          {course.faqs?.length > 0 && (
            <div data-testid="course-faqs">
              <h2 className="font-display text-2xl font-bold text-navy mb-5">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="border border-border rounded-lg divide-y divide-border">
                {course.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-0">
                    <AccordionTrigger className="px-5 hover:no-underline text-left font-medium text-navy">{f.q}</AccordionTrigger>
                    <AccordionContent className="px-5 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          <Disclaimer />
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-24 bg-white border border-border rounded-lg overflow-hidden shadow-sm" data-testid="enroll-card">
            {course.image ? (
              <img src={course.image} alt={course.title} referrerPolicy="no-referrer" className="h-40 w-full object-cover" />
            ) : (
              <div className="h-40 w-full bg-navy grain flex items-center justify-center"><span className="font-display text-gold text-lg font-bold">KRV Prime</span></div>
            )}
            <div className="p-6">
              <div className="flex items-baseline justify-between">
                <span className="font-display text-3xl font-bold text-navy">{free ? "Free" : `₹${course.price.toLocaleString("en-IN")}`}</span>
                {course.status === "coming_soon" && <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-navy text-gold">Coming Soon</span>}
              </div>
              <button
                onClick={handleAccess}
                disabled={busy}
                data-testid="enroll-button"
                className="w-full mt-5 h-12 rounded-md bg-navy text-white font-semibold text-sm hover:bg-navy-light transition-colors disabled:opacity-60"
              >
                {course.status === "coming_soon" ? "Notify Me"
                  : enrolled ? "Go to Classroom"
                  : busy ? "Processing…"
                  : free ? "Enroll Free"
                  : `Buy Course · ₹${course.price.toLocaleString("en-IN")}`}
              </button>
              <ul className="mt-6 space-y-3 text-sm text-slate-600">
                <li className="flex justify-between"><span className="text-slate-400">Level</span><span>{course.level}</span></li>
                <li className="flex justify-between"><span className="text-slate-400">Duration</span><span>{course.duration}</span></li>
                <li className="flex justify-between"><span className="text-slate-400">Lessons</span><span>{course.modules_count}</span></li>
                <li className="flex justify-between"><span className="text-slate-400">Language</span><span>{course.language}</span></li>
              </ul>
            </div>
          </div>
        </aside>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-0" data-testid="preview-modal">
          <DialogTitle className="sr-only">Lesson preview</DialogTitle>
          <div className="aspect-video w-full bg-black">
            {preview && (() => {
              const yt = ytId(preview.video_url);
              const vm = vimeoId(preview.video_url);
              if (preview.video_type === "file" && preview.video_file) {
                const src = preview.video_file.startsWith("http") ? preview.video_file : `${process.env.REACT_APP_BACKEND_URL}${preview.video_file}`;
                return <video controls autoPlay className="w-full h-full"><source src={src} /></video>;
              }
              if (vm) return <iframe title="preview" src={`https://player.vimeo.com/video/${vm}`} allow="autoplay; fullscreen" allowFullScreen className="w-full h-full" />;
              if (yt) return <iframe title="preview" src={`https://www.youtube.com/embed/${yt}?autoplay=1`} allow="autoplay; encrypted-media; fullscreen" allowFullScreen className="w-full h-full" />;
              return <div className="w-full h-full flex items-center justify-center text-slate-400">Preview unavailable.</div>;
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
